<template>
  <div class="account-area">
    <div class="account-area__sidebar">
      <div class="account-area__information">
        <h4 class="mb-1 text-lg">
          {{ user?.data?.first_name }} {{ user?.data?.last_name }}
        </h4>
        <div class="text-sm font-normal text-gray-500">{{ getBalance }}</div>
      </div>
      <div class="account-area__navigation">
        <NuxtLink
          v-for="item in sidebarNavigation"
          :key="item.path"
          :to="$localePath(item.path)"
          class="link"
          :class="{
            'link--highlighted': item.isHighlighted,
          }"
        >
          <span class="link__icon">
            <component :is="item.icon" class="link__icon-svg" />
            <span
              v-if="item.badge"
              class="link__badge"
              :aria-label="
                t('account.dateRequests.badgeLabel', { count: item.badge })
              "
            >
              {{ item.badge }}
            </span>
          </span>
          <span>{{ item.title }}</span>
        </NuxtLink>
      </div>
    </div>

    <div class="account-area__content">
      <div v-if="$slots.title" class="account-area__title">
        <slot name="title"></slot>
      </div>
      <div v-if="$slots.subtitle" class="account-area__subtitle">
        <slot name="subtitle"></slot>
      </div>

      <div class="account-area__inner">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useLocalePath } from '#imports'
import { formatPriceAmount } from '~/utils/currencyHelpers'

// icons
import AccountIcon1 from '~/assets/account/account-icon-1.svg?component'
import AccountIcon2 from '~/assets/account/account-icon-2.svg?component'
import AccountIcon3 from '~/assets/account/account-icon-3.svg?component'
import AccountIcon4 from '~/assets/account/account-icon-4.svg?component'
import AccountIcon5 from '~/assets/account/account-icon-5.svg?component'

const $localePath = useLocalePath()
const { t, locale } = useI18n()
const { user } = useAccount()
const { dateRequestCount, fetchDateRequests, dateRequests } =
  useAccountDateRequests()

onMounted(async () => {
  if (dateRequests.value.length) {
    return
  }

  try {
    await fetchDateRequests()
  } catch {
    // Badge is optional; ignore fetch errors in navigation.
  }
})

// account navigation links
const sidebarNavigation = computed(() => [
  {
    title: t('account.generalInfo.label'),
    path: '/account/',
    icon: AccountIcon1,
  },
  {
    title: t('account.orders.label'),
    path: '/account/orders',
    icon: AccountIcon2,
  },
  {
    title: t('account.userInfo.label'),
    path: '/account/user-info',
    icon: AccountIcon3,
  },
  {
    title: t('account.dateRequests.label'),
    path: '/account/date-requests',
    icon: AccountIcon5,
    badge: dateRequestCount.value > 0 ? dateRequestCount.value : undefined,
  },
  {
    title: t('account.referral.label'),
    path: '/account/referral-info',
    icon: AccountIcon4,
    isHighlighted: true,
  },
])

const getBalance = computed(() => {
  const balance = user.value?.data?.balance?.amount || 0

  return `
    ${formatPriceAmount(balance, locale.value)} ${t('account.balance')}
  `
})
</script>

<style lang="postcss" scoped>
.account-area {
  @apply flex flex-col md:flex-row gap-6 pt-8;

  &--loading {
    @apply animate-pulse;
  }

  &__sidebar {
    @apply w-full md:w-2/6;
  }

  &__information {
    @apply w-full p-6 rounded-lg;
    box-shadow: 0 0 20px 0 rgba(76, 87, 125, 0.06);
  }

  &__navigation {
    @apply flex flex-col gap-2 mt-6;

    .link {
      @apply px-4 py-2.5 font-medium rounded-lg text-teal-900 hover:bg-teal-50/50 duration-300 ease-in-out flex items-center gap-2.5;

      &--highlighted {
        @apply bg-[#faffee];
      }

      &.router-link-exact-active {
        @apply bg-teal-50;
      }

      &__icon {
        @apply relative inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center;
      }

      &__icon-svg {
        @apply h-[22px] w-[22px];
      }

      &__badge {
        @apply absolute -right-1.5 -top-1.5 min-w-[16px] rounded-full px-1 py-0.5 text-center text-[10px] font-semibold leading-none text-stone-800;
        background-color: #ffd4c8;
      }
    }
  }

  &__content {
    @apply w-full md:w-4/6 rounded-lg;
  }

  &__title {
    @apply mb-6 text-lg font-semibold;
    &:has(+ .account-area__subtitle) {
      @apply mb-1;
    }
  }

  &__subtitle {
    @apply font-normal text-gray-500 text-sm mb-6;
  }
}
</style>
